import {useApolloClient} from "@apollo/react-hooks"
import {assert} from "@prosay/common"
import {DateFromNow} from "@prosay/components"
import {translate} from "@prosay/i18n"
import {getLogger} from "@prosay/logging"
import React, {useCallback, useMemo} from "react"
import {FlatList, StyleSheet} from "react-native"
import {Avatar, Button, Card, Paragraph} from "react-native-paper"
import {Actions} from "react-native-router-flux"
import {createAcceptCaseResponse} from "../../data"
import {
    CaseInfoWithInitialMessagesFragment,
    GetConversationDocument,
    useAcceptCaseMutation,
} from "../../graphql"
import {useDate, useGetPendingCases} from "../../hooks"

const logger = getLogger(__filename)

const styles = StyleSheet.create({
    inquiryStartedDateText: {
        marginRight: 10,
    },
    pendingCaseActionButton: {
        flex: 1,
    },
})

export interface PendingCaseProps {
    case_: CaseInfoWithInitialMessagesFragment
}
export const PendingCase: React.FC<PendingCaseProps> = ({case_}) => {
    const {cache: apolloCache} = useApolloClient()
    const acceptCaseResponse = useMemo(
        () =>
            createAcceptCaseResponse(
                apolloCache,
                case_.title,
                case_.title,
                case_.topic.id,
            ),
        [apolloCache, case_.title, case_.topic.id],
    )

    const [mutate] = useAcceptCaseMutation({
        onCompleted: data => {
            if (!data.acceptCase?.id) {
                logger.warn`Could not get the id of the accepted case!`
                return
            }
            Actions.conversations()
            Actions.messages({
                id: data.acceptCase.id,
                title: data.acceptCase.title,
            })
        },
        optimisticResponse: acceptCaseResponse,
        refetchQueries: GetConversationDocument,
        variables: {id: case_.id},
    })

    // TODO: Add proper decline case functionality
    const declineCase = useCallback(() => {}, [])

    const date = useDate(case_.createdAt)

    const {title} = case_
    const subtitle = `${case_.topic.name}: ${case_.topic.category.name}`

    return (
        <Card>
            <Card.Title
                title={title}
                subtitle={subtitle}
                right={() => (
                    <DateFromNow
                        style={styles.inquiryStartedDateText}
                        {...{date}}
                    />
                )}
                left={props => (
                    <Avatar.Icon
                        {...props}
                        icon={case_.topic.icon ?? "briefcase"}
                    />
                )}
            />
            <Card.Content>
                <>
                    {case_.initialMessages
                        .filter(
                            ({content}) => content.__typename === "TextContent",
                        )
                        .map(({content, id}) => {
                            assert(
                                content.__typename === "TextContent",
                                `content typename should be TextContent but is ${content.__typename}`,
                            )
                            return (
                                <Paragraph key={id}>
                                    {content.content}
                                </Paragraph>
                            )
                        })}
                </>
            </Card.Content>
            <Card.Actions>
                <Button
                    icon="close"
                    style={styles.pendingCaseActionButton}
                    onPress={declineCase}>
                    {translate(
                        "ui.screen.acceptConversation.caseDeclineButton",
                    )}
                </Button>
                <Button
                    icon="check"
                    style={styles.pendingCaseActionButton}
                    onPress={mutate}>
                    {translate("ui.screen.acceptConversation.caseAcceptButton")}
                </Button>
            </Card.Actions>
        </Card>
    )
}

export interface AcceptConversationProps {}
export const AcceptConversation: React.FC<AcceptConversationProps> = () => {
    const {data, loading, refetch} = useGetPendingCases()

    return (
        <FlatList
            refreshing={loading}
            onRefresh={refetch}
            data={data?.getPendingCases ?? []}
            renderItem={({item}) => <PendingCase case_={item} />}
        />
    )
}
